import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, AlertCircle, Clock, CheckCircle2, 
  RefreshCw, TrendingUp, TrendingDown, Minus,
  Shield, Activity, Eye, FileWarning
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/services/api';

// Risk level config
const RISK_LEVEL_CONFIG = {
  CRITICAL: { color: 'bg-red-600/20 text-red-400 border-red-600/40', icon: AlertCircle, label: 'Critical' },
  HIGH: { color: 'bg-orange-600/20 text-orange-400 border-orange-600/40', icon: AlertTriangle, label: 'High' },
  MEDIUM: { color: 'bg-gold-600/20 text-gold-400 border-gold-600/40', icon: Clock, label: 'Medium' },
  LOW: { color: 'bg-blue-600/20 text-blue-400 border-blue-600/40', icon: Activity, label: 'Low' }
};

// Risk category labels
const CATEGORY_LABELS = {
  PENDING_TIMEOUT: 'Pending Timeout',
  INACTIVITY: 'Inactivity',
  SLOT_EXHAUSTION: 'Slot Exhaustion',
  DEADLINE_MISS: 'Deadline Miss',
  NEGOTIATION_STALL: 'Negotiation Stall',
  COMPLIANCE_VIOLATION: 'Compliance Issue',
  DEAL_AT_RISK: 'Deal at Risk',
  PROPERTY_ANOMALY: 'Property Anomaly'
};

// Trend icons
const TREND_ICONS = {
  increasing: TrendingUp,
  decreasing: TrendingDown,
  stable: Minus
};

// Single Alert Card
const AlertCard = ({ alert, onResolve, isResolving }) => {
  const levelConfig = RISK_LEVEL_CONFIG[alert.level] || RISK_LEVEL_CONFIG.LOW;
  const LevelIcon = levelConfig.icon;
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${levelConfig.color}`}>
          <LevelIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={levelConfig.color}>
              {levelConfig.label}
            </Badge>
            <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-xs">
              {CATEGORY_LABELS[alert.category] || alert.category}
            </Badge>
          </div>
          <p className="text-white text-sm font-medium truncate">{alert.title}</p>
          <p className="text-gray-400 text-xs mt-1 line-clamp-2">{alert.description}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">
              {alert.age_hours < 24 
                ? `${Math.round(alert.age_hours)}h ago` 
                : `${Math.round(alert.age_hours / 24)}d ago`}
            </span>
            {!alert.resolved && (
              <Button 
                size="sm" 
                variant="ghost"
                className="h-6 px-2 text-xs text-green-400 hover:bg-green-600/20"
                onClick={() => onResolve(alert.alert_id)}
                disabled={isResolving}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Resolve
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Summary Stats Row
const SummaryStats = ({ summary, loading }) => {
  const TrendIcon = TREND_ICONS[summary?.trend_24h] || Minus;
  const trendColor = summary?.trend_24h === 'increasing' ? 'text-red-400' : 
                     summary?.trend_24h === 'decreasing' ? 'text-green-400' : 'text-gray-400';
  
  return (
    <div className="grid grid-cols-5 gap-2 mb-4">
      <div className="text-center p-2 bg-red-900/20 border border-red-600/20 rounded">
        <div className="text-xl font-bold text-red-400">{loading ? '-' : summary?.critical_count || 0}</div>
        <div className="text-xs text-gray-500">Critical</div>
      </div>
      <div className="text-center p-2 bg-orange-900/20 border border-orange-600/20 rounded">
        <div className="text-xl font-bold text-orange-400">{loading ? '-' : summary?.high_count || 0}</div>
        <div className="text-xs text-gray-500">High</div>
      </div>
      <div className="text-center p-2 bg-gold-900/20 border border-gold-600/20 rounded">
        <div className="text-xl font-bold text-gold-400">{loading ? '-' : summary?.medium_count || 0}</div>
        <div className="text-xs text-gray-500">Medium</div>
      </div>
      <div className="text-center p-2 bg-blue-900/20 border border-blue-600/20 rounded">
        <div className="text-xl font-bold text-blue-400">{loading ? '-' : summary?.low_count || 0}</div>
        <div className="text-xs text-gray-500">Low</div>
      </div>
      <div className="text-center p-2 bg-white/5 border border-white/10 rounded">
        <div className={`text-xl font-bold ${trendColor} flex items-center justify-center gap-1`}>
          <TrendIcon className="w-4 h-4" />
          <span className="text-sm">{summary?.trend_24h || 'stable'}</span>
        </div>
        <div className="text-xs text-gray-500">24h Trend</div>
      </div>
    </div>
  );
};

// Main Risk Dashboard Component
const RiskDashboard = ({ entityType = null, entityId = null, compact = false }) => {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // all, critical, high

  const fetchRiskData = useCallback(async () => {
    setLoading(true);
    try {
      // If entity-specific, fetch alerts for that entity
      if (entityType && entityId) {
        const { data } = await api.get(`/api/risk/alerts/entity/${entityType}/${entityId}`);
        setAlerts(data.alerts || []);
        setSummary({
          total_alerts: data.total,
          critical_count: data.alerts?.filter(a => a.level === 'CRITICAL').length || 0,
          high_count: data.alerts?.filter(a => a.level === 'HIGH').length || 0,
          medium_count: data.alerts?.filter(a => a.level === 'MEDIUM').length || 0,
          low_count: data.alerts?.filter(a => a.level === 'LOW').length || 0,
          trend_24h: 'stable'
        });
      } else {
        // Fetch global summary and alerts
        const [summaryRes, alertsRes] = await Promise.all([
          api.get('/api/risk/summary'),
          api.get('/api/risk/alerts?limit=50')
        ]);
        setSummary(summaryRes.data);
        setAlerts(alertsRes.data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch risk data:', error);
      toast.error('Failed to load risk data');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchRiskData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchRiskData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchRiskData]);

  const handleResolve = async (alertId) => {
    setResolvingId(alertId);
    try {
      await api.post(`/api/risk/alerts/${alertId}/resolve`, {
        resolved_by: 'current_user',
        resolution_note: 'Resolved from dashboard'
      });
      toast.success('Alert resolved');
      fetchRiskData();
    } catch (error) {
      console.error('Failed to resolve alert:', error);
      toast.error('Failed to resolve alert');
    } finally {
      setResolvingId(null);
    }
  };

  const handleScan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/api/risk/scan');
      toast.success(`Scan complete: ${data.alerts_detected} alerts detected`);
      fetchRiskData();
    } catch (error) {
      console.error('Failed to run scan:', error);
      toast.error('Failed to run risk scan');
    }
  };

  // Filter alerts
  const filteredAlerts = alerts.filter(alert => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'critical') return alert.level === 'CRITICAL';
    if (activeFilter === 'high') return alert.level === 'CRITICAL' || alert.level === 'HIGH';
    return true;
  });

  // Compact view for widgets
  if (compact) {
    const criticalAlerts = alerts.filter(a => a.level === 'CRITICAL' || a.level === 'HIGH');
    
    return (
      <Card className="bg-[#1a1a2e] border-white/10 h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <Shield className="w-4 h-4 text-red-400" />
              Risk Monitor
            </CardTitle>
            <div className="flex items-center gap-1">
              {(summary?.critical_count || 0) > 0 && (
                <Badge className="bg-red-600/20 text-red-400 text-xs">
                  {summary.critical_count} critical
                </Badge>
              )}
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={fetchRiskData}
                className="h-6 w-6 text-gray-400"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="text-center p-2 bg-red-900/20 rounded">
              <div className="text-lg font-bold text-red-400">{summary?.critical_count || 0}</div>
              <div className="text-xs text-gray-500">Crit</div>
            </div>
            <div className="text-center p-2 bg-orange-900/20 rounded">
              <div className="text-lg font-bold text-orange-400">{summary?.high_count || 0}</div>
              <div className="text-xs text-gray-500">High</div>
            </div>
            <div className="text-center p-2 bg-gold-900/20 rounded">
              <div className="text-lg font-bold text-gold-400">{summary?.medium_count || 0}</div>
              <div className="text-xs text-gray-500">Med</div>
            </div>
            <div className="text-center p-2 bg-blue-900/20 rounded">
              <div className="text-lg font-bold text-blue-400">{summary?.low_count || 0}</div>
              <div className="text-xs text-gray-500">Low</div>
            </div>
          </div>
          
          {criticalAlerts.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-400 font-medium">Attention Required:</div>
              {criticalAlerts.slice(0, 3).map((alert) => (
                <div 
                  key={alert.alert_id}
                  className="p-2 bg-red-900/10 border border-red-600/20 rounded text-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white truncate flex-1">{alert.title}</span>
                    <Badge className="bg-red-600/30 text-red-300 text-xs ml-2">
                      {alert.level}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {criticalAlerts.length === 0 && !loading && (
            <div className="text-center py-4 text-gray-400 text-sm">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-400/50" />
              No critical risks
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full dashboard view
  return (
    <Card className="bg-[#1a1a2e] border-white/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-white">
            <Shield className="w-5 h-5 text-red-400" />
            Risk & Anomaly Dashboard
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleScan}
              className="bg-white/5 border-white/10"
              disabled={loading}
            >
              <Eye className="w-4 h-4 mr-2" />
              Run Scan
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={fetchRiskData}
              className="text-gray-400"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <SummaryStats summary={summary} loading={loading} />
        
        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4">
          <Button
            size="sm"
            variant={activeFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setActiveFilter('all')}
            className={activeFilter === 'all' ? 'bg-blue-600' : 'bg-white/5 border-white/10'}
          >
            All ({summary?.total_alerts || 0})
          </Button>
          <Button
            size="sm"
            variant={activeFilter === 'critical' ? 'default' : 'outline'}
            onClick={() => setActiveFilter('critical')}
            className={activeFilter === 'critical' ? 'bg-red-600' : 'bg-white/5 border-white/10'}
          >
            Critical ({summary?.critical_count || 0})
          </Button>
          <Button
            size="sm"
            variant={activeFilter === 'high' ? 'default' : 'outline'}
            onClick={() => setActiveFilter('high')}
            className={activeFilter === 'high' ? 'bg-orange-600' : 'bg-white/5 border-white/10'}
          >
            High+ ({(summary?.critical_count || 0) + (summary?.high_count || 0)})
          </Button>
        </div>
        
        {/* Alerts List */}
        <ScrollArea className="h-[400px]">
          {filteredAlerts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-400/30" />
              <p className="text-lg">No active risks</p>
              <p className="text-sm mt-2">All systems operating normally</p>
            </div>
          ) : (
            <div className="space-y-3 pr-4">
              {filteredAlerts.map((alert) => (
                <AlertCard 
                  key={alert.alert_id}
                  alert={alert}
                  onResolve={handleResolve}
                  isResolving={resolvingId === alert.alert_id}
                />
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Risk Score */}
        {summary && summary.total_alerts > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Platform Health Score</span>
              <span className="text-sm font-medium text-white">
                {Math.max(0, 100 - (summary.critical_count * 20) - (summary.high_count * 10) - (summary.medium_count * 5))}%
              </span>
            </div>
            <Progress 
              value={Math.max(0, 100 - (summary.critical_count * 20) - (summary.high_count * 10) - (summary.medium_count * 5))} 
              className="h-2"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskDashboard;
